import type { FieldErrors } from 'react-hook-form';

import { Draggable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

import { ErrorMessage, Icon } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { clsx } from '~/styles/utils';

import type { DocumentFormEntryLine, DocumentFormTextLine } from '../../types';
import type { LineDesktopProps } from './Line.types';

import { useFormWithLinesContext } from '../FormLines.context';
import { useSelectedContactLanguage } from '../FormLines.hooks';
import { useSectionContext } from '../Section/Section.context';
import { useLineContext } from '../SectionLines/SectionLines.context';
import { FormActivityType } from './FormActivityType/FormActivityType';
import { FormDescription } from './FormDescription/FormDescription';
import { FormDiscount } from './FormDiscount/FormDiscount';
import { FormPrice } from './FormPrice/FormPrice';
import { FormQuantity } from './FormQuantity/FormQuantity';
import { useIsFreeLine, useLineProduct, useLineTotal, useProductName, useShouldHaveTopBorder } from './Line.hooks';
import { LineActions } from './LineActions/LineActions';
import {
  actionsStyle,
  descriptionErrorStyle,
  descriptionStyle,
  discountedStyle,
  dragHandlerStyle,
  dragHandleWrapperStyle,
  errorCellStyle,
  fullRowStyle,
  lineCellStyle,
  lineStyle,
  priceAmountErrorStyle,
  priceAmountStyle,
  totalAmountStyle,
  totalStyle,
} from './LineDesktop.css';
import { ProductName } from './ProductName/ProductName';

export const LineDesktop = () => {
  const { section, sectionIndex } = useSectionContext();
  const { line, lineIndex } = useLineContext();
  const shouldHaveBorderTop = useShouldHaveTopBorder(sectionIndex, lineIndex);

  return (
    <Draggable draggableId={`${line.uuid}`} index={lineIndex}>
      {(provided, snapshot) => (
        <tr
          className={lineStyle}
          ref={provided.innerRef}
          {...provided.draggableProps}
          data-border-top={shouldHaveBorderTop || undefined}
          data-dragging={snapshot.isDragging || undefined}
          data-indented={section.isPhotoAlbum || undefined}
          data-line-type={line.type}
          data-testid="line"
        >
          {line.type === 'entry' ? (
            <EntryLineDesktop dragHandleProps={provided.dragHandleProps} />
          ) : (
            <TextLineDesktop dragHandleProps={provided.dragHandleProps} />
          )}
        </tr>
      )}
    </Draggable>
  );
};

const EntryLineDesktop = ({ dragHandleProps }: LineDesktopProps) => {
  const {
    formState: { errors },
    watch,
  } = useFormWithLinesContext();

  const { sectionIndex } = useSectionContext();
  const { isProductLine, lineFieldName, lineIndex } = useLineContext();

  const { formatCurrency, formatDecimal, formatPercentage } = useIntl();
  const { t } = useTranslation(['lines', 'validation']);

  const lineErrors = (errors.sections?.[sectionIndex]?.lines?.[lineIndex] as FieldErrors<DocumentFormEntryLine> | undefined) ?? null;
  const watchDiscountType = watch(`${lineFieldName}.discountType`);

  const { discountedTotal, total } = useLineTotal(lineFieldName);
  const isFreeLine = useIsFreeLine(lineFieldName);
  const product = useLineProduct(lineFieldName);
  const contactLanguage = useSelectedContactLanguage();
  const productName = useProductName(product, contactLanguage);

  return (
    <>
      <td className={lineCellStyle} data-invalid={!!lineErrors || undefined}>
        <div className={dragHandlerStyle}>
          <div {...dragHandleProps} className={dragHandleWrapperStyle}>
            <Icon name="Drag" />
          </div>
        </div>

        <div className={descriptionStyle} data-product={isProductLine || undefined}>
          {product !== null && <ProductName value={productName} />}

          <FormDescription />
        </div>

        <div className={priceAmountStyle}>
          <FormQuantity />

          <FormPrice />

          {watchDiscountType !== null && (
            <div className={fullRowStyle}>
              <FormDiscount />
            </div>
          )}

          <div className={fullRowStyle}>
            <FormActivityType />
          </div>
        </div>

        <div className={totalStyle}>
          <span className={clsx(totalAmountStyle, watchDiscountType !== null && discountedStyle)}>{formatCurrency(total)}</span>
          {watchDiscountType !== null && (
            <span className={totalAmountStyle}>{isFreeLine ? t('lines:discount.free') : formatCurrency(discountedTotal)}</span>
          )}
        </div>

        <div className={actionsStyle}>
          <LineActions />
        </div>
      </td>

      {lineErrors && (
        <td className={errorCellStyle}>
          {lineErrors.description && (
            <div className={descriptionErrorStyle}>
              <ErrorMessage>
                {lineErrors.description.type === 'required' && t('validation:required')}
                {lineErrors.description.type === 'validate' && t('validation:required')}
                {lineErrors.description.type === 'maxLength' &&
                  t('validation:maxLength', {
                    attribute: isProductLine ? t('lines:description.placeholderProduct') : t('lines:description.header'),
                    max: 255,
                  })}
              </ErrorMessage>
            </div>
          )}
          {(lineErrors.price || lineErrors.discountValue || lineErrors.quantity || lineErrors.activityType) && (
            <div className={priceAmountErrorStyle}>
              {lineErrors.quantity && (
                <ErrorMessage>
                  {lineErrors.quantity.type === 'required' && t('validation:requiredAttribute', { attribute: t('lines:quantity.header') })}
                  {lineErrors.quantity.type === 'min' &&
                    t('validation:min', { attribute: t('lines:quantity.header'), min: formatDecimal(0.01) })}
                  {lineErrors.quantity.type === 'max' &&
                    t('validation:max', { attribute: t('lines:quantity.header'), max: formatDecimal(100_000) })}
                </ErrorMessage>
              )}
              {lineErrors.price && (
                <ErrorMessage>
                  {lineErrors.price.type === 'required' && t('validation:requiredAttribute', { attribute: t('lines:price.header') })}
                  {lineErrors.price.type === 'min' &&
                    t('validation:min', { attribute: t('lines:price.header'), min: formatCurrency(0.01) })}
                  {lineErrors.price.type === 'max' &&
                    t('validation:max', { attribute: t('lines:price.header'), max: formatCurrency(100_000) })}
                </ErrorMessage>
              )}
              {lineErrors.discountValue && (
                <ErrorMessage>
                  {lineErrors.discountValue.type === 'required' &&
                    t('validation:requiredAttribute', { attribute: t('lines:discount.header') })}
                  {lineErrors.discountValue.type === 'min' &&
                    t('validation:min', {
                      attribute: t('lines:discount.header'),
                      min: watchDiscountType === 'percentage' ? formatPercentage(0.01) : formatCurrency(0.01),
                    })}
                  {lineErrors.discountValue.type === 'max' &&
                    (watchDiscountType === 'percentage'
                      ? t('validation:max', { attribute: t('lines:discount.header'), max: formatPercentage(1) })
                      : t('validation:maxDiscount'))}
                </ErrorMessage>
              )}
              {lineErrors.activityType && (
                <ErrorMessage>{lineErrors.activityType.type === 'required' && t('validation:required')}</ErrorMessage>
              )}
            </div>
          )}
        </td>
      )}
    </>
  );
};

const TextLineDesktop = ({ dragHandleProps }: LineDesktopProps) => {
  const {
    formState: { errors },
  } = useFormWithLinesContext();

  const { sectionIndex } = useSectionContext();
  const { lineIndex } = useLineContext();

  const { t } = useTranslation(['lines', 'validation']);

  const lineErrors = (errors.sections?.[sectionIndex]?.lines?.[lineIndex] as FieldErrors<DocumentFormTextLine> | undefined) ?? null;

  return (
    <>
      <td className={lineCellStyle} data-invalid={!!lineErrors || undefined}>
        <div className={dragHandlerStyle}>
          <div {...dragHandleProps} className={dragHandleWrapperStyle}>
            <Icon name="Drag" />
          </div>
        </div>

        <div className={descriptionStyle}>
          <FormDescription />
        </div>

        <div className={actionsStyle}>
          <LineActions />
        </div>
      </td>

      {lineErrors?.description && (
        <td className={errorCellStyle}>
          <div className={descriptionErrorStyle}>
            <ErrorMessage>
              {lineErrors.description.type === 'required' && t('validation:required')}
              {lineErrors.description.type === 'validate' && t('validation:required')}
              {lineErrors.description.type === 'maxLength' && t('validation:maxLength', { attribute: t('lines:textLine'), max: 255 })}
            </ErrorMessage>
          </div>
        </td>
      )}
    </>
  );
};
